import type { ProgressButtonState } from "@bankmonitor/bm-ui-kit";
import autobind from "autobind-decorator";
import type { ReactNode } from "react";
import React from "react";
import { Navigate } from "react-router-dom";
import imgLogin from "@asset/img/login.svg";
import imgMfa from "@asset/img/mfa.svg";
import Notifications from "@component/Notifications";
import LoginForm from "@page/auth/component/LoginForm";
// biome-ignore lint/style/useNamingConvention: a JSX component must stay capitalised or it renders as an intrinsic element
import MFAComponent from "@page/auth/component/MFAComponent";
import { signInForMfa } from "@page/auth/signInForMfa";
import AuthContext from "@service/AuthenticationContext";
import AuthenticationService from "@service/AuthenticationService";
import { Measurement } from "@util/measurement/Measurement";
import type { ReadableError } from "@util/ReadableError";
import type { ConsultantProfileData } from "@service/CommonService.type";
import type { AbstractPageProps } from "../AbstractPage";
import type { AbstractAuthPageState } from "./AbstractAuthPage";
import { AbstractAuthPage } from "./AbstractAuthPage";

interface State extends AbstractAuthPageState {
	loading: ProgressButtonState;
	initialValues: FormFields;
	showMFAComponent: boolean;
	consultantData: ConsultantProfileData;
}

interface FormFields {
	email: string;
	password: string;
}

// The portal tells the four reasons apart and they mean very different things, so the code is reported rather than a
// single generic value. UNKNOWN covers a failure that never reached the typed branches, such as a network error.
function loginErrorType(error: Error): string {
	return (error as ReadableError).errorCode ?? "UNKNOWN";
}

export default class LoginPage extends AbstractAuthPage<AbstractPageProps, State> {
	protected readonly pageTitle = "Ügyfélportál bejelentkezés";
	protected readonly pageLead = "Gyorsítsd fel az ügyintézés folyamatát!";

	declare context: React.ContextType<typeof AuthContext>;
	public static contextType = AuthContext;

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			loading: undefined,
			initialValues: {
				email: "",
				password: "",
			},
			showMFAComponent: false,
		};
	}

	public componentDidMount(): void {
		super.componentDidMount();

		this.checkMFA();
	}

	@autobind
	private checkMFA(): void {
		const { userData } = this.context;

		this.setState({
			showMFAComponent: userData && AuthenticationService.hasMFA(),
		});
	}

	@autobind
	private handleSubmit(values: FormFields): void {
		this.setState({
			loading: "loading",
			initialValues: values,
		});

		signInForMfa({ auth: this.context, promisePool: this.promisePool }, values)
			.then(() => {
				this.setState({
					loading: "done",
					showMFAComponent: true,
				});
			})
			.catch((error: Error) => {
				Measurement.sendEvent("BM_loginError", { action: "password", errorType: loginErrorType(error) });
				Notifications.showErrorMessage(error);

				this.setState({
					loading: "error",
				});
			})
			.finally(() => {
				this.promisePool.addTimeout().then(() =>
					this.setState({
						loading: undefined,
					}),
				);
			});
	}

	protected renderLeftColumn(): ReactNode {
		const { showMFAComponent } = this.state;

		if (showMFAComponent) {
			return (
				<div className="d-none d-md-block text-center">
					<img src={imgMfa} alt="mfa" width="75%" />
				</div>
			);
		}

		return (
			<div className="d-none d-md-block text-center">
				<img src={imgLogin} alt="login" width="75%" />
			</div>
		);
	}

	protected renderRightColumn(): ReactNode {
		const { loading, showMFAComponent } = this.state;

		const { userData } = this.context;

		if (showMFAComponent) {
			return <MFAComponent phone={userData?.phone} />;
		}

		return <LoginForm onSubmit={this.handleSubmit} loading={loading} />;
	}

	public render(): ReactNode {
		if (AuthenticationService.hasMFA()) {
			return <Navigate to="/" />;
		}

		return super.render();
	}
}
